import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { cloneDeep as _cloneDeep } from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import GCPService from '../../../../data/gcp-service';
import Auth from '../../../../data/auth-service';
import DataContext from '../../../../data/data-context';
import FileDragDrop from '../../../../components/drag-drop';

const useStyles = makeStyles(theme => ({
  panel: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  }
}));

const initialGCPState = {
  conf: { region: "" },
  user_creds: {
    auth_provider_x509_cert_url: "",
    auth_uri: "",
    client_email: "",
    client_id: "",
    client_x509_cert_url: "",
    private_key_id: "",
    project_id: "",
    token_uri: "",
    type: "",
    universe_domain: "",
    status: { verify: null }
  }
};

const GCPCredentials = () => {
  const location = useLocation();
  const classes = useStyles();
  const navigate = useNavigate();
  const { systemSettings, setSystemSettings } = useContext(DataContext);

  const [state, setState] = useState({
    gcp: initialGCPState,
    uploaded: false,
    verify: null,
    disableSave: true,
    edit: false
  });

  const resetState = () => {
    setState({
      gcp: initialGCPState,
      uploaded: false,
      verify: null,
      disableSave: true,
      edit: false
    });
  };

  const handleImport = async (data, file) => {
    try {
      resetState();

      if (!data || !file) {
        if (systemSettings.gcp?.user_creds.project_id) {
          const res = await GCPService.deleteGCPCreds();
          if (res === 200) {
            const updatedSettings = { ...systemSettings };
            updatedSettings.gcp.user_creds = initialGCPState.user_creds;
            setSystemSettings(updatedSettings);
            resetState();
          }
        }
        return;
      }

      setState(prev => ({ ...prev, uploaded: true }));
      const uploadResponse = await GCPService.uploadKeyFile(file);

      if (uploadResponse.status === 200) {
        const configResponse = await GCPService.getConfig();

        if (configResponse) {
          const newState = {
            gcp: configResponse,
            uploaded: true,
            verify: configResponse.user_creds?.status?.verify || false,
            disableSave: !configResponse.user_creds?.status?.verify
          };
          setState(newState);

          if (configResponse.user_creds?.status?.verify) {
            toast.success('Key Verified successfully');
          } else {
            toast.error('Key verification failed.');
          }
        } else {
          throw new Error('Failed to fetch GCP config.');
        }
      } else {
        setState(prev => ({ ...prev, verify: false }));
        throw new Error('Key file is not valid');
      }
    } catch (error) {
      console.error('Error during file import:', error);
      toast.error(error.message);
    }
  };

  const handleDelete = useCallback(async () => {
    try {
      if (systemSettings.gcp?.user_creds.project_id) {
        const res = await GCPService.deleteGCPCreds();
        if (res === 200) {
          const updatedSettings = { ...systemSettings };
          updatedSettings.gcp.user_creds = initialGCPState.user_creds;
          setSystemSettings(updatedSettings);
          resetState();
          toast.success("Deleted GCP Credentials successfully");
        }
      } else {
        resetState()
      }
    } catch (error) {
      toast.error('Error deleting GCP credentials');
    }
  }, [systemSettings, setSystemSettings]);

  const handleCancel = () => {
    navigate(state.uploaded && state.verify
      ? '/system/port_mirroring/gcp'
      : '/system/port_mirroring'
    );
  };

  const handleSave = () => {
    setSystemSettings(_cloneDeep({ gcp: state.gcp }));
    handleCancel();
  };

  const handleReturn = () => {
    handleCancel();
  }

  useEffect(() => {
    const fetchGCPConfig = async () => {
      if (systemSettings.gcp?.user_creds.project_id) {
        try {
          const res = await GCPService.getConfig();
          if (res) {
            setState({
              gcp: res,
              uploaded: true,
              verify: res.user_creds?.status?.verify || false,
              disableSave: !res.user_creds?.status?.verify,
              edit: true,
            });
          }
        } catch (error) {
          console.error("Error fetching GCP config:", error);
        }
      }
    };

    if (location && location?.state?.creds) {
      const data = location.state.creds;
      if (data.user_creds?.project_id) {
        setState({
          gcp: data,
          uploaded: true,
          verify: data.user_creds?.status?.verify || false,
          disableSave: !data.user_creds?.status?.verify,
          edit: true
        });
      }
    } else {
      fetchGCPConfig();
    }

  }, [systemSettings, location]);

  return (
    <Grid>
      <Paper elevation={3}>
        <Box p={5}>
          <Grid container spacing={5} justifyContent="space-between">
            <Grid item xs={12}>
              <Typography variant="h5">Configure Account Key</Typography>
              <Box mt={1}>
                <Typography variant="subtitle2">Upload your GCP service account key</Typography>
              </Box>
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Client Id"
                disabled
                fullWidth
                value={state.gcp?.user_creds?.client_id || ''}
              />
              <Box mt={3}>
                <TextField
                  label="Project Id"
                  disabled
                  fullWidth
                  value={state.gcp?.user_creds?.project_id || ''}
                />
              </Box>
              <Box mt={3}>
                <TextField
                  label="Client Email"
                  disabled
                  fullWidth
                  value={state.gcp?.user_creds?.client_email || ''}
                />
              </Box>
              <Box mt={3}>
                <TextField
                  label="Account Type"
                  disabled
                  fullWidth
                  value={state.gcp?.user_creds?.type || ''}
                />
              </Box>
            </Grid>
            <Grid item xs={5}>
              <FileDragDrop
                onImport={(data, file) => handleImport(data, file)}
                valid={state.verify}
                id="file-upload"
                isFileUploadedProp={state.uploaded}
              />

              <Box mt={4}>
                <Grid container justifyContent="flex-end">
                  {state.uploaded ? (
                    <Box mr={2}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleDelete}
                        startIcon={<DeleteIcon />}
                      >
                        Delete
                      </Button>
                    </Box>
                  ) : (
                    <Box mr={2}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                  {state.edit ? (
                    <Button
                      variant="contained"
                      disabled={state.disableSave}
                      color="primary"
                      onClick={handleReturn}
                    >
                      Return
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={state.disableSave}
                      color="primary"
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Grid>
  );
};

export default GCPCredentials;